import React, { useRef, useEffect, useState, useCallback } from 'react';
import { sanitizeHtml } from '../../utils/sanitize';
import { ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';
import { PageItem, Page } from '../../hooks/usePdfPageLayout';
import { SectionPosition } from '../../hooks/usePdfExportState';

// Component for reliable content cloning with proper timing
interface SectionContentProps {
  sectionId: string;
  clipStyle: React.CSSProperties;
  contentWidth: number;
  // Bumped whenever the source DOM changes (block removed/restored, inline edit)
  // so the clone re-reads innerHTML from the measurement container.
  revision: number;
}

const SectionContent: React.FC<SectionContentProps> = ({ sectionId, clipStyle, contentWidth, revision }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [content, setContent] = useState<string>('');
  const copyAttempts = useRef(0);

  useEffect(() => {
    copyAttempts.current = 0;

    const copyContent = () => {
      const sourceSection = document.querySelector(
        `.measurement-container [data-section-id="${sectionId}"]`
      );

      if (sourceSection) {
        const sourceHTML = sourceSection.innerHTML;
        if (sourceHTML && sourceHTML.trim()) {
          setContent(sourceHTML);
          return true;
        }
      }
      return false;
    };

    // Try immediately
    if (copyContent()) return;

    // Retry with increasing delays (100ms, 200ms, 400ms, 800ms)
    const retryWithDelay = (delay: number) => {
      const timeoutId = setTimeout(() => {
        copyAttempts.current++;
        if (!copyContent() && copyAttempts.current < 5) {
          retryWithDelay(delay * 2);
        }
      }, delay);
      return timeoutId;
    };

    const timeoutId = retryWithDelay(100);
    return () => clearTimeout(timeoutId);
  }, [sectionId, revision]);

  return (
    <div
      ref={contentRef}
      className="page-section-content"
      style={{
        ...clipStyle,
        width: contentWidth
      }}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
    />
  );
};

interface WysiwygPageContainerProps {
  children: React.ReactNode;
  pages: Page[];
  pageDimensions: { width: number; height: number };
  marginPixels: number;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  logoDataUrl?: string | null;
  logoPosition?: 'left' | 'center' | 'right';
  companyName?: string;
  headerText?: string;
  footerText?: string;
  sectionOrder: string[];
  selectedSections: Record<string, boolean>;
  sectionPositions: Map<string, SectionPosition>;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  onDragEnd: (sectionId: string, deltaX: number, deltaY: number, targetPageNumber?: number) => void;
  // When true, the user edits the measurement container (the print source)
  // directly: it is shown in flow and the paginated clone view is hidden.
  editMode?: boolean;
  // Bumped on block removal / restore to force the clones to re-read content.
  revision?: number;
}

export const WysiwygPageContainer: React.FC<WysiwygPageContainerProps> = ({
  children,
  pages,
  pageDimensions,
  marginPixels,
  zoom,
  onZoomChange,
  logoDataUrl,
  logoPosition = 'right',
  companyName,
  headerText,
  footerText,
  sectionOrder,
  selectedSections,
  sectionPositions,
  onReorder,
  onDragEnd,
  editMode = false,
  revision = 0
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const contentWidth = pageDimensions.width - (marginPixels * 2);
  const contentHeight = pageDimensions.height - (marginPixels * 2);
  const headerReservedHeight = logoDataUrl ? 80 : 0;

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    onZoomChange(Math.min(zoom + 0.1, 1.5));
  }, [zoom, onZoomChange]);

  const handleZoomOut = useCallback(() => {
    onZoomChange(Math.max(zoom - 0.1, 0.25));
  }, [zoom, onZoomChange]);

  // Page navigation
  const goToPage = useCallback((pageNum: number) => {
    const pageElement = containerRef.current?.querySelector(`[data-page="${pageNum}"]`);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Track current page on scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        const pageElements = container.querySelectorAll('[data-page]');
        let mostVisiblePage = 1;
        let maxVisibleHeight = 0;

        pageElements.forEach((el) => {
          const rect = el.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();

          const visibleTop = Math.max(rect.top, containerRect.top);
          const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);

          if (visibleHeight > maxVisibleHeight) {
            maxVisibleHeight = visibleHeight;
            mostVisiblePage = parseInt(el.getAttribute('data-page') || '1');
          }
        });

        setCurrentPage(mostVisiblePage);
      }, 100);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  const totalPages = pages.length || 1;

  // Render a single page with its content
  const renderPage = (page: Page) => {
    const isFirstPage = page.pageNumber === 1;
    const pageContentAreaHeight = isFirstPage ? contentHeight - headerReservedHeight : contentHeight;

    // Check if any item on this page is a continuation or has more
    const hasContinuation = page.items.some(item => item.isContinuation);
    const hasMore = page.items.some(item => item.hasMore);

    return (
      <div
        key={page.pageNumber}
        data-page={page.pageNumber}
        className="wysiwyg-page bg-white shadow-2xl relative"
        style={{
          width: pageDimensions.width,
          height: pageDimensions.height,
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Margin indicators - visual guides */}
        <div
          className="absolute pointer-events-none z-10"
          style={{
            top: marginPixels,
            left: marginPixels,
            right: marginPixels,
            bottom: marginPixels,
            border: '1px dashed rgba(25, 147, 229, 0.3)',
            borderRadius: 2
          }}
        />

        {/* Content area */}
        <div
          className="wysiwyg-page-content absolute"
          style={{
            top: marginPixels,
            left: marginPixels,
            width: contentWidth,
            height: contentHeight,
            overflow: 'hidden'
          }}
        >
          {/* Logo header on first page */}
          {isFirstPage && logoDataUrl && (
            <div
              className={`wysiwyg-page-header flex items-center pb-4 mb-4 border-b border-gray-200 ${
                logoPosition === 'left' ? 'justify-start' :
                logoPosition === 'center' ? 'justify-center' :
                'justify-end'
              }`}
              style={{ width: contentWidth, height: headerReservedHeight - 16 }}
            >
              {headerText && (
                <span className="text-xs text-gray-500 mr-3">{headerText}</span>
              )}
              <img
                src={logoDataUrl}
                alt="Company Logo"
                className="max-h-12 max-w-[180px] object-contain"
              />
            </div>
          )}

          {/* Section content viewport - render sections with positioning */}
          <div
            className="page-content-viewport relative"
            data-page-number={page.pageNumber}
            style={{
              height: pageContentAreaHeight,
              overflow: 'hidden'
            }}
          >
            {page.items.map((item) => {
              // Calculate clip for split sections
              const clipStyle: React.CSSProperties = {};
              if (item.clipStartY !== undefined && item.clipEndY !== undefined && item.totalSectionHeight) {
                const topClip = item.clipStartY;
                const bottomClip = item.totalSectionHeight - item.clipEndY;
                clipStyle.clipPath = `inset(${topClip}px 0 ${bottomClip}px 0)`;
                clipStyle.marginTop = -item.clipStartY;
              }

              return (
                <div
                  key={`${item.sectionId}-${page.pageNumber}-${item.isContinuation ? 'cont' : 'main'}`}
                  className="page-section-wrapper"
                  style={{
                    position: 'absolute',
                    top: item.offsetY,
                    left: 0,
                    width: contentWidth,
                    height: item.height,
                    overflow: 'hidden'
                  }}
                >
                  <SectionContent
                    sectionId={item.sectionId}
                    clipStyle={clipStyle}
                    contentWidth={contentWidth}
                    revision={revision}
                  />

                  {/* Continuation indicator at top */}
                  {item.isContinuation && (
                    <div className="absolute -top-5 left-0 right-0 flex items-center justify-center text-xs text-blue-600 bg-blue-50 rounded py-0.5 z-10">
                      ↑ Continued from previous page
                    </div>
                  )}

                  {/* Has more indicator at bottom */}
                  {item.hasMore && (
                    <div className="absolute -bottom-5 left-0 right-0 flex items-center justify-center text-xs text-blue-600 bg-blue-50 rounded py-0.5 z-10">
                      Continues on next page ↓
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Page footer */}
        <div
          className="absolute flex items-center justify-between text-xs text-gray-400"
          style={{
            bottom: marginPixels / 3,
            left: marginPixels,
            right: marginPixels
          }}
        >
          <div className="flex gap-2">
            {companyName && <span>{companyName}</span>}
            {footerText && <span className="text-gray-300">|</span>}
            {footerText && <span>{footerText}</span>}
          </div>
          <span>Page {page.pageNumber} of {totalPages}</span>
        </div>

        {/* Page number badge */}
        <div className="absolute -top-3 -right-3 bg-[#1993e5] text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg z-20">
          {page.pageNumber}
        </div>

        {/* Continuation indicator at top of page */}
        {hasContinuation && (
          <div className="absolute top-1 left-1/2 -translate-x-1/2 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-b z-20">
            ↑ Continued
          </div>
        )}

        {/* Continuation indicator at bottom of page */}
        {hasMore && (
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-t z-20">
            Continues ↓
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="wysiwyg-container flex flex-col h-full">
        {/* Measurement container - renders all content for measurement, and is
            also the DOM that window.print() outputs. In edit mode we surface it
            so the user edits the real print source directly. */}
        <div
          className={`measurement-container ${editMode ? 'measurement-container--editing flex-1 overflow-auto bg-gray-400 p-8' : ''}`}
          style={editMode ? {
            position: 'static',
            visibility: 'visible',
            pointerEvents: 'auto',
            left: 'auto',
            top: 'auto',
            width: 'auto'
          } : {
            position: 'absolute',
            visibility: 'hidden',
            width: contentWidth,
            pointerEvents: 'none',
            left: -9999,
            top: 0
          }}
        >
          <div
            style={editMode ? {
              width: pageDimensions.width,
              minHeight: pageDimensions.height,
              margin: '0 auto',
              background: 'white',
              padding: marginPixels,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            } : undefined}
          >
            {children}
          </div>
        </div>

        {/* Toolbar */}
        <div className="wysiwyg-toolbar flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-200 sticky top-0 z-10">
          {/* Zoom controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleZoomOut}
              className="p-1.5 hover:bg-gray-200 rounded transition-colors"
              title="Zoom out"
            >
              <ZoomOut className="w-4 h-4 text-gray-600" />
            </button>
            <span className="text-sm text-gray-700 min-w-[60px] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={handleZoomIn}
              className="p-1.5 hover:bg-gray-200 rounded transition-colors"
              title="Zoom in"
            >
              <ZoomIn className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {/* Page indicator */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => goToPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="p-1.5 hover:bg-gray-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages}
              className="p-1.5 hover:bg-gray-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {/* Info */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">
              {pageDimensions.width === 794 ? 'A4' : 'Letter'} • {Math.round(marginPixels / 3.78)}mm margins
            </span>
          </div>
        </div>

        {/* Pages container - scrollable area with gray background. Hidden in
            edit mode, where the user edits the measurement container directly. */}
        <div
          ref={containerRef}
          className={`wysiwyg-pages-container flex-1 overflow-auto bg-gray-400 p-8 ${editMode ? 'hidden' : ''}`}
        >
          <div
            className="wysiwyg-pages-wrapper flex flex-col items-center gap-8"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'top center',
              width: `${100 / zoom}%`,
              paddingBottom: 32
            }}
          >
            {/* Render all pages */}
            {pages.length > 0 ? (
              pages.map(page => renderPage(page))
            ) : (
              // Fallback single page when no pages calculated
              renderPage({
                pageNumber: 1,
                items: [],
                contentHeight: 0
              })
            )}

            {/* Info panel */}
            <div className="text-center text-sm text-gray-600 bg-gray-200 px-4 py-2 rounded-lg shadow mt-4">
              <p className="font-medium">{totalPages} page{totalPages !== 1 ? 's' : ''} • {Math.round(marginPixels / 3.78)}mm margins</p>
              <p className="text-xs text-gray-500 mt-1">
                Content: {contentWidth}×{contentHeight}px •
                Page: {pageDimensions.width}×{pageDimensions.height}px
              </p>
            </div>
          </div>
        </div>

        {/* Styles */}
        <style>{`
          .wysiwyg-page {
            transition: box-shadow 0.2s;
          }

          .wysiwyg-page:hover {
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35),
                        0 0 0 1px rgba(0, 0, 0, 0.15) !important;
          }

          .wysiwyg-pages-container {
            scrollbar-width: thin;
            scrollbar-color: #94a3b8 #cbd5e1;
          }

          .wysiwyg-pages-container::-webkit-scrollbar {
            width: 10px;
          }

          .wysiwyg-pages-container::-webkit-scrollbar-track {
            background: #cbd5e1;
          }

          .wysiwyg-pages-container::-webkit-scrollbar-thumb {
            background: #94a3b8;
            border-radius: 5px;
          }

          .wysiwyg-pages-container::-webkit-scrollbar-thumb:hover {
            background: #64748b;
          }

          .page-section-wrapper {
            transition: box-shadow 0.2s;
          }

          /* Print styles - show original content, hide paginated display */
          @media print {
            /* Show measurement container (original content) for printing */
            .measurement-container {
              position: static !important;
              visibility: visible !important;
              left: auto !important;
              width: 100% !important;
            }

            /* Hide the paginated page display - browser CSS handles pagination */
            .wysiwyg-pages-container {
              display: none !important;
            }

            .wysiwyg-toolbar {
              display: none !important;
            }

            /* Neutralize the edit-mode page framing so print uses @page margins */
            .measurement-container--editing {
              padding: 0 !important;
              background: white !important;
              overflow: visible !important;
            }
            .measurement-container--editing > div {
              width: 100% !important;
              min-height: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              box-shadow: none !important;
            }
          }
        `}</style>
    </div>
  );
};

export default WysiwygPageContainer;
